import React, {useMemo} from "react";
import propsAreEqual from "react-fast-compare";
import {ClientProfile} from "@/api/dto/client-profile";
import {
  PhoneHistoryTableContainer,
  InsuranceHistoryTableContainer,
  HistoryTableLabel,
  HistoryTable,
  HistoryRow,
  DemographicsRow,
  DemographicColumn,
  StyledValue,
  Wrapper,
  DemographicsWrapper,
  TableContainer,
  TableContainerRight,
} from "@clients/profile_page/ClientDemographicContent.styles";
import DisplayHelpers from "@/helpers/display_helpers";
import CopyButton from "@common/CopyButton";
import PatientPhoneNumber from "@common/PatientPhoneNumber";
import Spacer from "patient-ping-remedy/packages/spacer";
import {css} from "@emotion/css";
import ClientDemographicsNode from "@clients/profile_page/ClientDemographicsNode";
import ClientPhotoPreview from "@clients/profile_page/ClientPhotoPreview";
import FileHelpers from "@/helpers/file_helpers";
import {useStateConfig} from "@/hooks/useStateConfig";

type ModalProps = {
  patient?: ClientProfile;
  isLoading?: boolean;
  isEditAvailable?: boolean;
  onClick?(): void;
}

type InsuranceHistory = {
  date: string;
  insurances: string[];
  reportedBy: string;

  // Currently the order of encounters is not sorted by encounter date in encounter insurances
  //TODO Fix:  https://hbuco.atlassian.net/browse/PNGDAT-2081
  recentEventDate: string;
}

function mergeInsurances(patient?: ClientProfile) : InsuranceHistory[]{
  const patientRosterInsurances = patient?.patientRosterInsurances || [];
  const patientEncounterInsurances = patient?.patientEncounterInsurances || [];
  const insurances: InsuranceHistory[] = [];

  patientRosterInsurances.forEach((rosterInsurance) => {
    insurances.push({
      date: DisplayHelpers.formatDateFromDateOnlyString(rosterInsurance.uploadDate),
      insurances: Array.from(new Set(rosterInsurance.insurances)),
      reportedBy: rosterInsurance.groupName,
      recentEventDate: rosterInsurance.uploadDate
    });
  });

  patientEncounterInsurances.forEach((encounterInsurance) => {
    let encounterDate = "";
    if(encounterInsurance.encounterStartDate){
      encounterDate = DisplayHelpers.formatDateFromDateOnlyString(encounterInsurance.encounterStartDate);
    }
    if(encounterInsurance.encounterEndDate){
      if(encounterInsurance.encounterStartDate) {
        encounterDate = encounterDate + " - ";
      }
      encounterDate = encounterDate +  DisplayHelpers.formatDateFromDateOnlyString(encounterInsurance.encounterEndDate);
    }

    insurances.push({
      date: encounterDate,
      insurances: Array.from(new Set(encounterInsurance.insurances.map(insurance => insurance.insuranceCompanyName))),
      reportedBy: encounterInsurance.groupName,
      recentEventDate: encounterInsurance.encounterEndDate || encounterInsurance.encounterStartDate
    });
  });

  return insurances
    .filter((insurance) => insurance.date !== "" && insurance.insurances.length > 0)
    .sort((a, b) => {
      return new Date(b.recentEventDate) > new Date(a.recentEventDate) ? 1 : -1;
    });
}

const ClientDemographicContent = ({ patient, isLoading, isEditAvailable, onClick }: ModalProps) => {
  const stateConfig = useStateConfig();
  const phoneNumbers = patient?.patientPhoneNumbers || [];
  const insurances = useMemo(() => mergeInsurances(patient), [patient]);
  const relatedPersons = patient?.extendedDemographics?.nextOfKin || [];
  const showRelatedPersons = stateConfig?.features?.relatedPersonsEnabled && relatedPersons.length > 0;
  const photo = useMemo(
    () => patient?.client?.photo
      ? new Blob([FileHelpers.base64ToArrayBuffer(patient.client.photo)], { type: 'image/png' })
      : null,
    [patient],
  );

  return (
    <Wrapper>
      <ClientPhotoPreview
        photo={photo}
        isLoading={isLoading}
        isEditable={isEditAvailable}
        onClick={onClick}
      />
      <DemographicsWrapper>
        <DemographicsRow>
          <DemographicColumn>
            <ClientDemographicsNode
              label="Ethnicity"
              value={
                <StyledValue>
                  {DisplayHelpers.toTitlecase(patient?.extendedDemographics?.ethnicity) || '-'}
                </StyledValue>
              }
            />
          </DemographicColumn>
          <DemographicColumn>
            <ClientDemographicsNode
              label="Race"
              value={
                <StyledValue>
                  {DisplayHelpers.toTitlecase(patient?.extendedDemographics?.race) || '-'}
                </StyledValue>
              }
            />
          </DemographicColumn>
          <DemographicColumn>
            <ClientDemographicsNode
              label="Primary language"
              value={
                <StyledValue>
                  {DisplayHelpers.humanize(patient?.extendedDemographics?.language) || '-'}
                </StyledValue>
              }
            />
          </DemographicColumn>
          <DemographicColumn>
            <ClientDemographicsNode
              label="Veteran status"
              value={
                <StyledValue>
                  {DisplayHelpers.humanize(patient?.extendedDemographics?.veteranStatus) || '-'}
                </StyledValue>
              }
            />
          </DemographicColumn>
          <DemographicColumn>
            <ClientDemographicsNode
              label="Marital status"
              value={
                <StyledValue>
                  {DisplayHelpers.humanize(patient?.extendedDemographics?.maritalStatus) || '-'}
                </StyledValue>
              }
            />
          </DemographicColumn>
        </DemographicsRow>
        <HistoryRow>
          <PhoneHistoryTableContainer>
            <HistoryTableLabel>
              Phone number(s) last 12 months <Spacer itemWidth={"10px"}/>
            </HistoryTableLabel>
            <HistoryTable>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Phone</th>
                  <th>Reported by</th>
                </tr>
              </thead>
              <tbody>
                {phoneNumbers.length > 0 ? (
                  phoneNumbers.map((phoneNumber, index) => {
                    const e164 = DisplayHelpers.formatE164PhoneNumber(phoneNumber.phone);
                    return (
                      <tr key={index}>
                        <td>{DisplayHelpers.formatDateFromDateOnlyString(phoneNumber.updatedDate)?.trim() || '-'}</td>
                        <td>
                          <PatientPhoneNumber phoneNumber={phoneNumber}>
                            {e164 && (
                              <CopyButton
                                value={e164}
                                ariaLabel="Copy phone number"
                                errorMessage="Unable to copy phone number. Your browser may not support copying."
                              />
                            )}
                          </PatientPhoneNumber>
                        </td>
                        <td>
                          {phoneNumber.groupName || '-'}
                          {DisplayHelpers.cleanGroupTypeKey(phoneNumber.groupType) && ` (${phoneNumber.groupType})`}
                          {phoneNumber.currentOrganization && ' (Created at Your Organization)'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
              </tbody>
            </HistoryTable>
          </PhoneHistoryTableContainer>

          <InsuranceHistoryTableContainer>
            <HistoryTableLabel>
              Encounter insurance(s) reported last 12 months <Spacer itemWidth={"10px"}/>
            </HistoryTableLabel>
            <HistoryTable>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Insurance</th>
                  <th>Reported by</th>
                </tr>
              </thead>
              <tbody>
                {insurances.length > 0 ? (
                  insurances.map((insurance, index) => (
                    <tr key={index}>
                      <td>{insurance.date || '-'}</td>
                      <td>
                        {insurance.insurances.map((ins, insIndex) => (
                          <div className={css({paddingBottom: "0.4rem"})} key={insIndex}>{ins || '-'}</div>
                        ))}
                      </td>
                      <td>{insurance.reportedBy || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
              </tbody>
            </HistoryTable>
          </InsuranceHistoryTableContainer>
        </HistoryRow>
        <HistoryRow>
          {showRelatedPersons && (
            <TableContainer>
              <HistoryTableLabel>
                Related person(s) last 12 months
              </HistoryTableLabel>
              <HistoryTable>
                <thead>
                  <tr>
                    <th>Name & Relationship</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Reported by</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedPersons.map((person, index) => (
                    <tr key={index}>
                      <td>
                        {DisplayHelpers.formatName(person.firstName, undefined, person.lastName) || ''}
                        {person.relationship ? ` (${DisplayHelpers.humanize(person.relationship)})` : ''}
                      </td>
                      <td>{person.phoneNumbers?.[0] ? DisplayHelpers.formatPhoneNumber(person.phoneNumbers[0]) : ''}</td>
                      <td>{person.email || ''}</td>
                      <td>{person.reportedBy?.groupName || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </HistoryTable>
            </TableContainer>
          )}
          <TableContainerRight>
            <HistoryTableLabel>
              Reactions
            </HistoryTableLabel>
            <HistoryTable>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Treatment</th>
                  <th>Allergy Reaction</th>
                </tr>
              </thead>
              <tbody>
                {(patient?.extendedDemographics?.allergies && patient.extendedDemographics.allergies.length > 0) ? (
                  patient.extendedDemographics.allergies.map((allergy, index) => (
                    <tr key={index}>
                      <td>{allergy.code || ''}</td>
                      <td>{allergy.description || ''}</td>
                      <td>{allergy.reaction || ''}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
              </tbody>
            </HistoryTable>
          </TableContainerRight>
        </HistoryRow>
      </DemographicsWrapper>
    </Wrapper>
  );
};

export default React.memo(ClientDemographicContent, propsAreEqual);
